import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { FiSave, FiX } from 'react-icons/fi';
import { categoryService } from '../../services/categoryService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { toast } from 'react-hot-toast';

const CategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  // Fetch category if editing
  const { data: category, isLoading } = useQuery(
    ['category', id],
    () => categoryService.getById(id),
    { enabled: !!id }
  );

  useEffect(() => {
    if (category) {
      setValue('name', category.name);
      setValue('description', category.description);
    }
  }, [category, setValue]);

  const createMutation = useMutation(
    (data) => categoryService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        toast.success('បង្កើតប្រភេទដោយជោគជ័យ');
        navigate('/admin/categories');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចបង្កើតប្រភេទបានទេ');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => categoryService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        toast.success('កែប្រែប្រភេទដោយជោគជ័យ');
        navigate('/admin/categories');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចកែប្រែប្រភេទបានទេ');
      },
    }
  );

  const onSubmit = (data) => {
    if (id) {
      updateMutation.mutate({ id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  if (id && isLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'កែប្រែប្រភេទ' : 'បន្ថែមប្រភេទថ្មី'}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white border border-gray-200 p-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ឈ្មោះប្រភេទ *
              </label>
              <input
                {...register('name', { 
                  required: 'សូមបញ្ចូលឈ្មោះប្រភេទ',
                  minLength: { value: 2, message: 'ឈ្មោះត្រូវតែមានយ៉ាងតិច ២ តួអក្សរ' },
                  maxLength: { value: 50, message: 'ឈ្មោះត្រូវតែតិចជាង ៥០ តួអក្សរ' }
                })}
                className={`w-full px-4 py-2 border border-gray-200 focus:outline-none focus:border-red-600 ${
                  errors.name ? 'border-red-500' : 'bg-gray-50'
                }`}
                placeholder="បញ្ចូលឈ្មោះប្រភេទ"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ការពិពណ៌នា
              </label>
              <textarea
                {...register('description')}
                rows="4"
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 focus:outline-none focus:border-red-600"
                placeholder="បញ្ចូលការពិពណ៌នាប្រភេទ (ស្រេចចិត្ត)"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/categories')}
            className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
          >
            <FiX className="mr-2" /> បោះបង់
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading || updateMutation.isLoading}
            className="bg-red-600 text-white px-4 py-2 flex items-center hover:bg-red-700 transition-colors disabled:bg-gray-400"
          >
            <FiSave className="mr-2" />
            {createMutation.isLoading || updateMutation.isLoading ? 'កំពុងរក្សាទុក...' : 'រក្សាទុកប្រភេទ'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;